/**
 * PWA launch chooser (TD-016): shown by PortalStartRoute when more than
 * one portal session is stored on this device — e.g. client at one
 * business, creator at another. Lists local sessions only; workspaces the
 * user never logged into on this device don't appear (explicit non-goal,
 * see the 2026-07-09 spec).
 */
import { useNavigate } from "react-router-dom";
import { ClientIcon, ChevronRightIcon } from "@/components/icons";
import type { PortalSessionSummary } from "@/core/auth/listPortalSessions";
import { portalSurfacePath } from "@/core/auth/portalSurface";
import { LoginPage } from "../components/LoginPage";
import { LOGIN_HEADING, LOGIN_PICK_HINT, LOGIN_PICK_ROW, LOGIN_TEXT } from "../loginStyles";

const ROLE_LABELS: Record<string, string> = {
  client: "Portale cliente",
  creator: "Portale creator",
  editor: "Portale editor",
};

interface PortalWorkspaceChooserProps {
  sessions: PortalSessionSummary[];
}

export const PortalWorkspaceChooser: React.FC<PortalWorkspaceChooserProps> = ({
  sessions,
}) => {
  const navigate = useNavigate();

  return (
    <LoginPage>
      <div className="mb-(--login-gap)">
        <h1 className={LOGIN_HEADING}>Scegli il tuo spazio</h1>
        <p className={LOGIN_TEXT}>Hai accesso a più spazi di lavoro.</p>
      </div>

      <div className="space-y-[12px]">
        {sessions.map((session) => (
          <button
            key={session.clientId}
            type="button"
            onClick={() =>
              navigate(
                portalSurfacePath(session.role, session.clientId),
                { replace: true }
              )
            }
            className={`${LOGIN_PICK_ROW} flex items-center gap-[12px]`}
          >
            <ClientIcon className="w-[24px] h-[24px] text-gray-500 shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block truncate">
                {session.clientName || session.clientId}
              </span>
              <span className={LOGIN_PICK_HINT}>
                {ROLE_LABELS[session.role] || ROLE_LABELS.client}
              </span>
            </span>
            <ChevronRightIcon className="w-[20px] h-[20px] text-gray-400 shrink-0" />
          </button>
        ))}
      </div>
    </LoginPage>
  );
};
